import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { ProblemDetailMobile } from '@/public/features/problem/ui/ProblemDetailMobile'
import { ProblemDetailPc } from '@/public/features/problem/ui/ProblemDetailPc'
import { api } from '@/shared/lib/api'
import { URGENCY_LABEL, makeLabeler, useCommonCodes, useTargetCodes } from '@/shared/lib/codes'
import {
  DESIRED_PERIOD_LABEL,
  IMPACT_SCOPE_LABEL,
} from '@/public/features/problem/constants/problemOptions'

export function ProblemDetailPage() {
  const { id } = useParams<{ id: string }>()
  const problem = useQuery({
    queryKey: ['problem-card', id],
    queryFn: () => api.problemCard(id ?? ''),
    // `summary`·`keywords` 는 관리자가 언제든 고치는 값이라 캐시를 두지 않는다(계약 2026-09-03).
    staleTime: 0,
    enabled: Boolean(id),
  })

  const categoryLabel = makeLabeler(useCommonCodes('category').data)
  const targetLabel = makeLabeler(useTargetCodes().data)
  const helpTypeLabel = makeLabeler(useCommonCodes('help_type').data)

  const p = problem.data
  const unknown = <span className="text-black-100">확인 필요</span>

  return (
    <>
      <ProblemDetailMobile query={problem} />
      <ProblemDetailPc id={id} query={problem} />

      <div className="desktop-screen">
      <Link className="mb-4 inline-block text-label text-black-100" to="/problems">
        ← 시민문제카드 목록
      </Link>
      <QueryState isPending={problem.isPending} error={problem.error}>
        {p ? (
          <article className="rounded-[10px] border border-white-300 bg-white-50 p-7">
            <div className="mb-5 border-b border-white-300 pb-5">
              <div className="mb-2 text-caption text-white-900">
                {p.region.name}{' '}
                <span className={[
                    'inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    p.urgency === 'high'
                      ? 'bg-black-600 text-white-50'
                      : p.urgency === 'mid'
                        ? 'bg-white-200 text-black-500'
                        : 'border border-white-400 bg-transparent text-white-900',
                  ].join(' ')}>
                  {URGENCY_LABEL[p.urgency] ?? p.urgency}
                </span>
              </div>
              <h1 className="m-0 mb-1.5 text-headline-sm font-bold tracking-[-0.02em]">{p.title}</h1>
            </div>

            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5 text-body-sm">
              <dt>분야</dt>
              <dd>{p.categoryCodes?.length ? p.categoryCodes.map(categoryLabel).join(' · ') : unknown}</dd>

              <dt>대상</dt>
              <dd>{p.targetCodes.map(targetLabel).join(' · ')}</dd>

              <dt>필요한 도움</dt>
              <dd>{p.helpTypeCodes?.length ? p.helpTypeCodes.map(helpTypeLabel).join(' · ') : unknown}</dd>

              <dt>영향 범위</dt>
              <dd>{p.impactScope ? (IMPACT_SCOPE_LABEL[p.impactScope] ?? p.impactScope) : unknown}</dd>

              <dt>희망 시기</dt>
              <dd>{p.desiredPeriod ? (DESIRED_PERIOD_LABEL[p.desiredPeriod] ?? p.desiredPeriod) : unknown}</dd>

              <dt>어려운 점</dt>
              <dd>{p.barrierNote ?? unknown}</dd>

              <dt>필요한 자원</dt>
              <dd>{p.neededResourceNote ?? unknown}</dd>
            </dl>

            <div className="rounded-lg bg-white-200 px-4 py-3.5 text-label text-black-500" style={{ marginTop: 24 }}>
              이 문제에 맞는 기업카드를 찾아 연결하는 기능은 준비 중입니다.
            </div>
          </article>
        ) : null}
      </QueryState>
      </div>
    </>
  )
}
